/**
 * Constraints Service - Fetches constraint metadata (CHECK, DEFAULT) for tables
 */

import type { DatabaseEngineId } from '@/core/engines';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';

export interface ConstraintInfo {
  name: string;
  constraintType: 'CHECK' | 'DEFAULT';
  definition: string;
  columnName?: string;
}

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function getRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Maps CHECK constraint rows from tableConstraints query to ConstraintInfo[]
 */
export function mapCheckConstraintRows(rows: Record<string, unknown>[]): ConstraintInfo[] {
  const checkRows = rows.filter((row) => {
    const type = toStr(getRowValue(row, ['constraint_type']));
    return type === 'CHECK';
  });

  // Group by constraint name (deduplicate rows that may appear for multi-column checks)
  const seen = new Map<string, ConstraintInfo>();

  for (const row of checkRows) {
    const name = toStr(getRowValue(row, ['constraint_name']));
    if (!name || seen.has(name)) continue;

    const definition = toStr(getRowValue(row, ['check_definition']));
    const columnName = toStr(getRowValue(row, ['column_name']));

    seen.set(name, {
      name,
      constraintType: 'CHECK',
      definition,
      columnName: columnName || undefined,
    });
  }

  return Array.from(seen.values());
}

/**
 * Maps DEFAULT constraint rows from tableDefaultConstraints query to ConstraintInfo[]
 */
export function mapDefaultConstraintRows(rows: Record<string, unknown>[]): ConstraintInfo[] {
  const result: ConstraintInfo[] = [];

  for (const row of rows) {
    const name = toStr(getRowValue(row, ['constraint_name']));
    if (!name) continue;

    const definition = toStr(getRowValue(row, ['default_definition']));
    const columnName = toStr(getRowValue(row, ['column_name']));

    result.push({
      name,
      constraintType: 'DEFAULT',
      definition,
      columnName: columnName || undefined,
    });
  }

  return result;
}

/**
 * Format a constraint's display name in SSMS style
 * - CHECK: CK_PositivePrice: ([Price] > 0)
 * - DEFAULT: DF_CreatedDate: (getdate()) on CreatedDate
 */
export function formatConstraintDisplayName(constraint: ConstraintInfo): string {
  if (constraint.constraintType === 'DEFAULT' && constraint.columnName) {
    return `${constraint.name}: ${constraint.definition} on ${constraint.columnName}`;
  }
  return `${constraint.name}: ${constraint.definition}`;
}

export async function getConstraints(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  engine?: DatabaseEngineId | string
): Promise<{ constraints: ConstraintInfo[]; error?: string }> {
  const queries = getQueries(engine);

  try {
    // Run both queries in parallel
    const [checkResult, defaultResult] = await Promise.all([
      executeQueryV2({
        connectionId,
        sql: queries.tableConstraints(schema, tableName),
        database,
        executionId: createExplorerExecutionId(connectionId),
      }),
      executeQueryV2({
        connectionId,
        sql: queries.tableDefaultConstraints(schema, tableName),
        database,
        executionId: createExplorerExecutionId(connectionId),
      }),
    ]);

    if (!checkResult.success) {
      const errorMsg = checkResult.error?.message || 'Unknown error';
      console.error(`[ConstraintsService] Error getting check constraints: ${errorMsg}`);
      return { constraints: [], error: errorMsg };
    }

    if (!defaultResult.success) {
      const errorMsg = defaultResult.error?.message || 'Unknown error';
      console.error(`[ConstraintsService] Error getting default constraints: ${errorMsg}`);
      return { constraints: [], error: errorMsg };
    }

    const checkRows = checkResult.resultSets?.[0]?.rows || [];
    const defaultRows = defaultResult.resultSets?.[0]?.rows || [];

    const checkConstraints = mapCheckConstraintRows(checkRows);
    const defaultConstraints = mapDefaultConstraintRows(defaultRows);

    // Merge and sort by type then name
    const constraints = [...checkConstraints, ...defaultConstraints].sort((a, b) => {
      if (a.constraintType !== b.constraintType) {
        return a.constraintType.localeCompare(b.constraintType);
      }
      return a.name.localeCompare(b.name);
    });

    return { constraints };
  } catch (error) {
    console.error('[ConstraintsService] Error in getConstraints:', error);
    return {
      constraints: [],
      error: error instanceof Error ? error.message : 'Unknown error getting constraints',
    };
  }
}
